import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ModalComponent from "../modal";
import InputComponent from "../input";
import CheckboxRadioComponent from "../checkbox-radio";
import SelectDropdownSecondaryComponent from "../select-dropdown-secondary";
import ButtonComponent from "../button";
import { providerInstitutionConstant, statusOptions } from "../../constants/user-constants";
import { useFormik } from "formik";
import { roleSchema } from "../../helpers/schemas";
import { getPermissionsListById } from "../../redux/slices/permissions-list-slice";
import { transformToCheckboxTreeFormat } from "../../helpers/permissionsTree";
import CheckboxTreeComponent from "../checkbox-tree";
import { useLocation } from "react-router";
import TextareaComponent from "../textarea";
import { toast } from "react-toastify";
import { ActionsContainer, FieldContainer, Form, FormError, Label } from "./add-update-role-modal.style";

const AddUpdateRoleModal = ({ data, close, label, handleFormChange, handleCheckboxTreeChange, handleFormSubmit, viewOnly, update, fromMakerChecker, slide }) => {
    const location = useLocation();
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.loginUserReducer);
    const { institutions } = useSelector((state) => state.institutionsListReducer);
    const { permissions } = useSelector((state) => state.permissionsListReducer);

    // const notProvider = user?.institution?.value !== providerInstitutionConstant?.value;
    const isProvider = (user?.institution?.value === providerInstitutionConstant?.value) && (user?.superAdmin);
    const isChecker = location.pathname.includes('checker');
    const isDisabledAll = isChecker || viewOnly;

    const [nodes, setNodes] = useState([]);
    const [oldData] = useState(data);
    const [hasChanges, setHasChanges] = useState(false);
    const [selectedDisabled, setSelectedDisabled] = useState(null);

    const { handleChange, handleBlur, handleSubmit, validateForm, touched, errors, dirty } = useFormik({
        initialValues: data,
        validationSchema: roleSchema({ isChecker, fromMakerChecker }),
        onSubmit: fromMakerChecker ? () => {} : () => {
            const hasPermissionChanges = !data.permissions.every(permission => oldData.permissions.includes(permission)) || !oldData.permissions.every(permission => data.permissions.includes(permission));

            if (hasChanges || hasPermissionChanges) {
                handleFormSubmit();
            } else {
                toast.info('No changes found');
            }
        },
    });

    const handleAcceptReject = async (isAccept) => {
        const formErrors = await validateForm();

        if (Object.keys(formErrors).length < 1) {
            handleFormSubmit(isAccept);
        } else {
            handleSubmit();
        }
    };

    const fields = [
        // {
        //     label: 'Code',
        //     type: 'text',
        //     placeholder: 'Set Code',
        //     name: 'code',
        //     value: data.code,
        //     disabled: isDisabledAll || update,
        // },
        {
            label: 'Name',
            type: 'text',
            placeholder: 'Enter Name',
            name: 'descr',
            value: data.descr,
            disabled: isDisabledAll,
        },
        {
            label: 'Status',
            type: 'select',
            options: statusOptions,
            name: 'roleStatus',
            value: data.roleStatus,
            disabled: isDisabledAll,
        },
        {
            label: 'Institution',
            type: 'select',
            options: institutions,
            name: 'institution',
            value: data.institution,
            disabled: isDisabledAll || update || !isProvider,
        },
        {
            label: 'Permissions',
            type: 'checkboxTree',
            options: nodes,
            name: 'permissions',
            value: data.permissions,
            hide: !data.institution,
            fullCol: true,
            disabled: isDisabledAll,
        },
        ...(fromMakerChecker ? [{
            label: 'Comments',
            type: 'textarea',
            placeholder: 'Enter Comments',
            name: 'reason',
            value: data?.reason,
            fullCol: true,
        }] : []),
        {
            type: 'action',
            actions: fromMakerChecker ? [
                { label: 'Reject', type: 'button', color: 'error', outline: true, action: () => handleAcceptReject(false) },
                { label: isChecker ? 'Approve' : 'Resend', type: 'button', action: () => handleAcceptReject(true) }
            ] : [
                { label: viewOnly ? 'Close' : 'Cancel', type: 'button', outline: !viewOnly, action: close },
                ...(!viewOnly ? [{ label: update ? 'Update' : 'Submit', type: 'submit' }] : [])
            ],
            fullCol: true,
        }
    ];

    useEffect(() => {
        if (data?.institution && data?.institution?.length > 0) {
            dispatch(getPermissionsListById({ user, id: data?.institution }));
        }
    }, [data?.institution]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (permissions && permissions !== undefined && permissions.length > 0 && data.institution) {
            const permissionsTreeData = transformToCheckboxTreeFormat(permissions);
            setNodes(permissionsTreeData);
        }
    }, [permissions]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setHasChanges(dirty);
    }, [dirty]);

    // Helper function to get all parent permission IDs for a given permission
    const getParentPermissionIds = (permissionId, permissionsArray) => {
        const parents = [];
        const permission = permissionsArray.find(p => p.id === permissionId);
        
        if (permission && permission.parentId && permission.parentId !== -1) {
            parents.push(permission.parentId);
            // Recursively get parent's parents
            parents.push(...getParentPermissionIds(permission.parentId, permissionsArray));
        }
        
        return parents;
    };

    // Helper function to get all child permission IDs for a given permission
    const getChildPermissionIds = (permissionId, permissionsArray) => {
        const children = [];
        const directChildren = permissionsArray.filter(p => p.parentId === permissionId);
        
        directChildren.forEach(child => {
            children.push(child.id);
            // Recursively get children's children
            children.push(...getChildPermissionIds(child.id, permissionsArray));
        });
        
        return children;
    };

    // Enhanced checkbox tree change handler that handles parent-child relationships
    const handleEnhancedCheckboxTreeChange = (value, checked) => {
        if (!permissions) return;

        let updatedPermissions = [...data.permissions];

        if (checked) {
            // Add the permission if not already present
            if (!updatedPermissions.includes(value)) {
                updatedPermissions.push(value);
            }
            
            // Auto-check all parent permissions
            const parentIds = getParentPermissionIds(value, permissions);
            parentIds.forEach(parentId => {
                if (!updatedPermissions.includes(parentId)) {
                    updatedPermissions.push(parentId);
                }
            });
        } else {
            // Remove the permission
            updatedPermissions = updatedPermissions.filter(id => id !== value);
            
            // Auto-uncheck all child permissions
            const childIds = getChildPermissionIds(value, permissions);
            updatedPermissions = updatedPermissions.filter(id => !childIds.includes(id));
        }

        // Update the form data
        handleFormChange({
            target: {
                name: 'permissions',
                value: updatedPermissions
            }
        });
    };

    useEffect(() => {
        if (!data?.permissions || !permissions) return;

        const makers = permissions.filter(permission => permission.name.toLowerCase().includes('maker'));
        const checkers = permissions.filter(permission => permission.name.toLowerCase().includes('checker'));
        const makerIds = makers.map(permission => permission.id);
        const checkerIds = checkers.map(permission => permission.id);

        const checkerParentsIds = permissions.filter(permission =>
            permission.parentId &&
            !permission.name.toLowerCase().includes('maker') &&
            !permission.name.toLowerCase().includes('checker') &&
            checkers.find(checker => checker.parentId === permission.parentId)
        ).map(e => e.id);

        const isChecker = checkerIds.some(id => data?.permissions.includes(id));
        const isMaker = makerIds.some(id => data?.permissions.includes(id));

        if (isMaker) {
            setSelectedDisabled(checkerIds);
        } else if (isChecker) {
            data.permissions.forEach(permissionId => {
                if (checkerParentsIds.includes(permissionId)) {
                    handleCheckboxTreeChange(permissionId, false);
                }
            });
            setSelectedDisabled([...makerIds, ...checkerParentsIds]);
        } else {
            setSelectedDisabled(null);
        }
    }, [data.permissions, permissions, handleCheckboxTreeChange, handleFormChange]);

    return (
        <ModalComponent close={close} label={label} width={700} slide={slide}>
            {/* MODAL BODY */}
            <Form onSubmit={handleSubmit}>
                {
                    fields && fields.length > 0 && fields.map((field, index) => (
                        <Fragment key={index}>
                            {
                                ['text', 'number', 'email', 'password'].includes(field.type) && !field.hide &&
                                <FieldContainer $fullcol={field.fullCol}>
                                    {/* <Label>{field.label}</Label> */}
                                    <InputComponent
                                        label={field.label}
                                        type={field.type}
                                        placeholder={field.placeholder}
                                        onBlur={handleBlur}
                                        name={field.name}
                                        value={field.value}
                                        onChange={(e) => { handleChange(e); handleFormChange(e) }}
                                        disabled={field.disabled}
                                        mui
                                    />
                                    {
                                        touched[field.name] && errors[field.name] && (
                                            <FormError>{errors[field.name]}</FormError>
                                        )
                                    }
                                </FieldContainer>
                            }

                            {
                                ['checkbox', 'radio'].includes(field.type) && !field.hide &&
                                <FieldContainer $fullcol={field.fullCol}>
                                    <Label>{field.label}</Label>
                                    <CheckboxRadioComponent
                                        type={field.type}
                                        label={field.innerLabel}
                                        name={field.name}
                                        checked={field.value}
                                        onChange={(e) => { handleChange(e); handleFormChange(e) }}
                                        disabled={field.disabled}
                                    />
                                    {
                                        touched[field.name] && errors[field.name] && (
                                            <FormError>{errors[field.name]}</FormError>
                                        )
                                    }
                                </FieldContainer>
                            }

                            {
                                field.type === 'select' && !field.hide &&
                                <FieldContainer $fullcol={field.fullCol}>
                                    {/* <Label>{field.label}</Label> */}
                                    <SelectDropdownSecondaryComponent
                                        label={field.label}
                                        options={field.options}
                                        name={field.name}
                                        value={field.value}
                                        onChange={(e) => { handleChange(e); handleFormChange(e) }}
                                        disabled={field.disabled}
                                        mui
                                    />
                                    {
                                        touched[field.name] && errors[field.name] && (
                                            <FormError>{errors[field.name]}</FormError>
                                        )
                                    }
                                </FieldContainer>
                            }

                            {/* {
                                field.type === 'select' && !field.hide &&
                                <FieldContainer $fullcol={field.fullCol}>
                                    <Label>{field.label}</Label>
                                    <SelectDropdownComponent
                                        options={field.options}
                                        name={field.name}
                                        value={field.value}
                                        onChange={(e) => { handleChange(e); handleFormChange(e) }}
                                        disabled={field.disabled}
                                    />
                                    {
                                        touched[field.name] && errors[field.name] && (
                                            <FormError>{errors[field.name]}</FormError>
                                        )
                                    }
                                </FieldContainer>
                            } */}

                            {
                                field.type === 'checkboxTree' && !field.hide &&
                                <FieldContainer $fullcol={field.fullCol}>
                                    <Label>{field.label}</Label>
                                    <CheckboxTreeComponent
                                        nodes={field.options}
                                        handleCheckboxTreeChange={handleEnhancedCheckboxTreeChange}
                                        selectedValues={field.value}
                                        update={update}
                                        disabled={field.disabled}
                                        selectedDisabled={selectedDisabled}
                                    />
                                </FieldContainer>
                            }

                            {
                                field.type === 'textarea' && !field.hide &&
                                <FieldContainer $fullcol={field.fullCol}>
                                    {/* <Label>Comments</Label> */}
                                    <TextareaComponent
                                        label='Comments'
                                        placeholder={field.placeholder}
                                        onBlur={handleBlur}
                                        name={field.name}
                                        value={field.value}
                                        onChange={(e) => { handleChange(e); handleFormChange(e) }}
                                        disabled={field.disabled}
                                        mui
                                    />
                                    {
                                        touched[field.name] && errors[field.name] && (
                                            <FormError>{errors[field.name]}</FormError>
                                        )
                                    }
                                </FieldContainer>
                            }

                            {
                                field.type === 'action' && field.actions.length > 0 &&
                                <FieldContainer $fullcol={field.fullCol}>
                                    <ActionsContainer>
                                        {
                                            field.actions.map((button, index) => (
                                                <ButtonComponent key={index} label={button.label} type={button.type} outline={button.outline} color={button.color} onClick={button.action} />
                                            ))
                                        }
                                    </ActionsContainer>
                                </FieldContainer>
                            }
                        </Fragment>
                    ))
                }
            </Form>
        </ModalComponent>
    );
};

export default AddUpdateRoleModal;